import * as React from 'react';

import {
  Badge,
  DataGridColumn,
  LocalizedDate,
  Money,
  Row,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { InvoiceStatusBadge } from '../../_shared/InvoiceStatusBadge';
import { InvoiceAccountColumnFilter } from '../filters/InvoiceAccountColumnFilter';
import { InvoiceStatusColumnFilter } from '../filters/InvoiceStatusColumnFilter';
import { InvoiceTypeColumnFilter } from '../filters/InvoiceTypeColumnFilter';
import { InvoiceActionMenu } from '../InvoiceActionMenu';
import { InvoiceGroupCell } from '../InvoiceGroupCell';

import {
  $columnSelectedAccountIds,
  $columnSelectedStatuses,
  $columnSelectedTypes,
} from '~/modules/finance/_state/financeInvoices.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';
import { InvoiceRecord } from '~/modules/finance/types';

type Args = {
  onPay: (invoices: InvoiceRecord | InvoiceRecord[]) => void;
  onVoid: (invoice: InvoiceRecord) => void;
  onDoubleClick?: (record: InvoiceRecord) => void;
  grouped: boolean;
};

export const useInvoicesListColumns = ({
  onPay,
  onVoid,
  onDoubleClick,
  grouped,
}: Args) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const columnSelectedStatuses = useAtomValue($columnSelectedStatuses);
  const columnSelectedTypes = useAtomValue($columnSelectedTypes);
  const columnSelectedAccountIds = useAtomValue($columnSelectedAccountIds);

  // Memoized values
  const columns: DataGridColumn<InvoiceRecord>[] = React.useMemo(() => {
    const allColumns: DataGridColumn<InvoiceRecord>[] = [
      {
        key: 'documentNumber',
        header: t.columns.invoiceNumber,
        cell: ({ row: record }) => record.documentNumber,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
        grow: grouped ? 1 : undefined,
        groupCell: (group) => <InvoiceGroupCell {...group} />,
      },
      {
        key: 'accountName',
        header: t.columns.account,
        resizable: true,
        cell: ({ row: record }) => record.accountName,
        columnFilter: ({ close }) => (
          <InvoiceAccountColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedAccountIds.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'type',
        header: t.columns.type,
        cell: ({ row: record }) => (
          <Badge>{t.enums.invoiceType[record.type] ?? record.type}</Badge>
        ),
        columnFilter: ({ close }) => (
          <InvoiceTypeColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedTypes.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'status',
        header: t.columns.status,
        sortable: true,
        cell: ({ row: record }) => (
          <InvoiceStatusBadge status={record.status} />
        ),
        columnFilter: ({ close }) => (
          <InvoiceStatusColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedStatuses.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'openAmount',
        header: t.columns.balanceOwed,
        cell: ({ row: record }) =>
          record.openAmount ? (
            <Row xEnd>
              <Money {...record.openAmount} />
            </Row>
          ) : (
            '-'
          ),
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'documentDate',
        header: t.columns.documentDate,
        sortable: true,
        cell: ({ row: record }) =>
          record.documentDate ? (
            <LocalizedDate date={record.documentDate} dateStyle="medium" />
          ) : (
            '-'
          ),
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row: record }) => (
          <InvoiceActionMenu
            invoice={record}
            onReceivePayment={onPay}
            onVoid={onVoid}
            onViewDetails={(invoice) => onDoubleClick?.(invoice)}
          />
        ),
      },
    ];

    if (grouped) {
      return allColumns.filter(
        (col) => col.key !== 'accountName' && col.key !== 'accountType',
      );
    }

    return allColumns;
  }, [
    columnSelectedAccountIds,
    columnSelectedStatuses,
    columnSelectedTypes,
    grouped,
    onDoubleClick,
    onPay,
    onVoid,
    t,
  ]);

  return { columns };
};
